import logger from '../../utils/logger';
import { platformStorage } from '../../utils/storage';
import type { ApiResponse, TelemetryReading } from '../../types';
import { api } from './client';

export const deviceApi = {
  getDevices: async (orgId: string) => {
    const params = orgId ? { orgId } : {};
    const response = await api.get(`/devices`, { params });
    return response.data;
  },

  getAll: async () => {
    const user = await platformStorage.getItem('user_data');
    if (!user) {
      throw new Error('User not found');
    }
    const userData = typeof user === 'string' ? JSON.parse(user) : user;
    const orgId = userData.orgId;
    return deviceApi.getDevices(orgId);
  },

  getDeviceDetail: async (deviceId: string) => {
    const response = await api.get(`/devices/${deviceId}/detail`);
    return response.data;
  },

  getDeviceRecentReadings: async (
    deviceId: string,
    hours: number = 24,
  ): Promise<ApiResponse<TelemetryReading[]>> => {
    try {
      const endTime = new Date().toISOString();
      const startTime = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();

      const response = await api.get(`/devices/${deviceId}/telemetry`, {
        params: { startTime, endTime, limit: 1000 },
      });

      return { success: true, data: response.data };
    } catch (error: any) {
      logger.error('❌ Error fetching recent readings:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  },

  createDevice: async (data: any) => {
    const response = await api.post('/devices', data);
    return response.data;
  },

  updateDevice: async (deviceId: string, data: any) => {
    const response = await api.patch(`/devices/${deviceId}`, data);
    return response.data;
  },

  deleteDevice: async (deviceId: string) => {
    logger.log('🗑️ Deleting device:', deviceId);
    const response = await api.delete(`/devices/${deviceId}`);
    logger.log('✅ Device deleted successfully:', deviceId);
    return response.data;
  },

  sendDeviceCommand: async (deviceId: string, command: string, params?: Record<string, any>) => {
    logger.log('🎮 Sending device command:', { deviceId, command, params });
    const response = await api.post(`/devices/${deviceId}/command`, { command, ...params });
    logger.log('✅ Device command sent:', response.data);
    return response.data;
  },
};
